import React, { useContext, useEffect, useState } from "react";

import { Error } from "@mui/icons-material";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  useTheme,
} from "@mui/material";

import {
  GetUserOrganisationsResponse,
  User,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { Region } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import useDebouncedEffect from "../../../../helpers/hooks/use-debounced-effect";
import { VALID_REGIONS } from "api/constants";
import { getUser, getUserOrgs } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import { ToMigrationRegion } from "constants/workflow";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface CopyTemplateDialogProps {
  handleTargetUserId: (targetUserId: string) => void;
  handleTargetOrgId: (targetOrgId: string) => void;
  handleTargetRegion: (targetRegion: Region) => void;
  handleCopyTemplateDialogBtn: (isDisabled: boolean) => void;
  templateId: string;
}

const CopyTemplateDialog = ({
  handleTargetUserId,
  handleTargetOrgId,
  handleTargetRegion,
  handleCopyTemplateDialogBtn,
  templateId,
}: CopyTemplateDialogProps) => {
  const theme = useTheme();
  const [targetUserId, setTargetUserId] = useState("");
  const [targetOrgId, setTargetOrgId] = useState("");
  const [targetUserData, setTargetUserData] = useState<User | undefined>();
  const [targetOrgData, setTargetOrgData] =
    useState<GetUserOrganisationsResponse>();
  const [targetOrgValid, setTargetOrgValid] = useState(false);
  const [targetRegion, setTargetRegion] = useState("");

  const { showError } = useContext(SnackbarContext);

  const getTargetOrgData = async () => {
    setTargetOrgData(undefined);

    getUserOrgs(
      {
        userId: toUUID(targetUserId),
      },
      callOptionsHeaders(targetRegion, targetUserId),
    )
      .then((orgDataResp) => {
        setTargetOrgData(orgDataResp);
      })
      .catch(showError);
  };

  const handleSetTargetUserId = (value: string | User | null) => {
    setTargetOrgValid(false);
    if (!value) {
      return;
    }
    if (typeof value === "string") {
      return setTargetUserId(value);
    }
    if (value && typeof value === "object") {
      return setTargetUserId(value.id);
    }
  };

  const handleRegionChange = (event: SelectChangeEvent<string>) => {
    setTargetRegion(event.target.value as string);
    handleTargetRegion(ToMigrationRegion(event.target.value as string));
  };

  const getTargetUserLabel = () => {
    return getTargetUserName();
  };

  const getTargetUserName = () => {
    if (!targetOrgValid) {
      return "Target User Id";
    } else {
      return `${targetUserData?.firstName} ${
        targetUserData?.lastName
      } (${getTargetOrgName()})`;
    }
  };

  const getTargetOrgName = () => {
    if (targetOrgValid) {
      return `${targetOrgData?.organisations[0].name}`;
    }
    return "";
  };

  const generateUserIdHelperText = () => {
    if (targetRegion == "") return "Set target data center first";
    if (!targetUserData) return "Valid user ID required";
    if (!targetOrgValid) return "Specified user has invalid org";
  };

  const isValidUUIDString = (str: string) => {
    const uuidRegex = /^user_[0-9a-f]{32}$/i;
    return uuidRegex.test(str);
  };

  const displayTargetRegionMenuItems = () => {
    return VALID_REGIONS.map((region) => {
      return (
        <MenuItem value={region} key={`region-${region}`}>
          {region.toUpperCase()}
        </MenuItem>
      );
    });
  };

  useDebouncedEffect(
    () => {
      if (!isValidUUIDString(targetUserId)) {
        return;
      }
      setTargetUserData(undefined);
      getUser(
        {
          userId: targetUserId,
        },
        callOptionsHeaders(targetRegion, targetUserId),
      )
        .then((res) => {
          setTargetUserData(res);
          if (res?.orgId) {
            setTargetOrgId(res.orgId);
            getTargetOrgData();
          }
        })
        .catch((err) => {
          showError(err);
        });
      handleTargetUserId(targetUserId);
    },
    500,
    [targetUserId],
  );

  useEffect(() => {
    handleTargetOrgId(targetOrgId);
    handleCopyTemplateDialogBtn(!targetOrgValid);
    // eslint-disable-next-line
  }, [targetOrgValid]);

  useEffect(() => {
    const isValidOrg = targetOrgData?.organisations?.length === 1;
    setTargetOrgValid(isValidOrg);
    // eslint-disable-next-line
  }, [targetOrgData]);

  return (
    <>
      <Grid container direction="row" spacing={1} style={{ minWidth: 350 }}>
        <Grid item xs={12} container direction="column">
          <Box
            bgcolor={theme.palette.warning.main}
            borderRadius="5px"
            border={1}
            borderColor={theme.palette.secondary.main}
            marginBottom={1}
          >
            <Grid container alignItems="center">
              <Grid item xs={2} container justifyContent="center">
                <Error fontSize="large" />
              </Grid>
              <Grid item xs={10} style={{ paddingRight: "20px" }}>
                <p>
                  You are about to copy a template {templateId} to{" "}
                  <b>{getTargetUserName()}</b>. This operation will create a new
                  template that preserves the structure of the template but{" "}
                  <b> will generate all new internal IDs</b>. Any GRS referenced
                  by this template <b>will also be copied</b> (with new GRS ID).
                  Other artefacts referenced by the template (such as GRS,
                  media) are not copied (yet).
                  <br />
                </p>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="target-data-center-label">
              Target Data Center
            </InputLabel>
            <Select
              labelId="select-target-region-label"
              id="select-target-region"
              onChange={handleRegionChange}
              fullWidth
              value={targetRegion}
              label="Target Data Center"
              data-testid="select-target-region"
              inputProps={{
                "data-testid": "select-target-region-value",
              }}
              size="small"
            >
              {displayTargetRegionMenuItems()}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            error={!targetOrgValid}
            helperText={generateUserIdHelperText()}
            id="targetUserId-label"
            label={getTargetUserLabel()}
            placeholder="Target User Id"
            value={targetUserId}
            onChange={(event) => {
              handleSetTargetUserId(event.target.value.trim());
            }}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{
              "data-anchor": "targetUserId-input",
              "data-testid": "targetUserId-input",
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default CopyTemplateDialog;
